import { Kbd, ListItem, UnorderedList } from '@chakra-ui/react';
import { getGroupPalette } from '@resistapp/client/components/shared/palettes';
import { useAssayContext } from '@resistapp/client/contexts/assay-context';
import { useChartDownload } from '@resistapp/client/contexts/chart-download-context';
import { ContextualTipId, useContextualTipContext } from '@resistapp/client/contexts/contextual-tip-context';
import { Filters, handleFiltersSelectionWithKeys } from '@resistapp/client/data-utils/filter-data/filter';
import { GeneGrouping, L2Target, defaultGroupingOptions } from '@resistapp/common/assays';
import type { SampleStatus } from '@resistapp/common/types';
import { scaleOrdinal } from '@visx/scale';
import type React from 'react';
import { useEffect, useRef } from 'react';
import { ChartHotkeyInstructions } from '../../tooltips/chart-hotkey-instructions';
import { BaseLegend } from './base-legend';
import { KeysPressOptions, Legend } from './legend';

interface GeneLegendProps {
  availableGroups: string[];
  filters: Filters;
  setGrouping: (grouping: GeneGrouping) => void;
  toggleGeneGroup: (group: L2Target | L2Target[], only: boolean) => void;
  disableKeys?: boolean;
  header?: string;
  defaultValue?: GeneGrouping;
  description?: string;
  legendHeight?: string;
  topHeight?: string;
  showOptions?: boolean;
  id?: string;
  updateSampleStatuses?: (newStatus: SampleStatus, environmentId?: number) => void;
  /** Ref that will be attached to the first legend label for contextual onboarding tips */
  tipAnchorRef?: React.RefObject<HTMLElement>;
}

export function GeneLegend({
  availableGroups,
  filters,
  disableKeys,
  header,
  defaultValue = 'l2Target',
  toggleGeneGroup,
  setGrouping,
  description,
  legendHeight,
  topHeight,
  showOptions,
  id,
  updateSampleStatuses,
  tipAnchorRef,
}: GeneLegendProps) {
  const { markInteraction, setTipEnabled } = useContextualTipContext();
  const { allGeneGroups } = useAssayContext();
  const previousSelection = useRef<string | undefined>();
  const options = defaultGroupingOptions;
  const selectedGroupingOption = options.find(o => o.value === filters.selectedTargetGrouping)?.value || defaultValue;
  const { downloadChartAndLegend } = useChartDownload();

  const reversedGroups = [...availableGroups].reverse();
  const palette = getGroupPalette(reversedGroups, filters.selectedTargetGrouping, allGeneGroups);

  const colorScale = scaleOrdinal<string, string>({
    domain: reversedGroups,
    range: palette,
  });
  const getOpacity = (label: string) => (filters.selectedTargets.includes(label as L2Target) ? 1 : 0.4);
  const onClick = (label: string | number, keys: KeysPressOptions) => {
    const [newGroups, removeOldSelections] = handleFiltersSelectionWithKeys(
      filters.selectedTargets,
      reversedGroups,
      label,
      previousSelection.current,
      keys,
      Boolean(disableKeys),
    );
    previousSelection.current = previousSelection.current && keys.shift ? previousSelection.current : label.toString();
    toggleGeneGroup(newGroups as L2Target[], removeOldSelections);

    // Record interaction for contextual tip (gene legend focus)
    markInteraction(ContextualTipId.RESEARCH_GENE_LEGEND_FOCUS);
  };
  const labelFormat = (label: string | number) => String(label);

  // Enable the gene legend focus tip while this component is mounted
  useEffect(() => {
    setTipEnabled(ContextualTipId.RESEARCH_GENE_LEGEND_FOCUS, true);
    return () => {
      setTipEnabled(ContextualTipId.RESEARCH_GENE_LEGEND_FOCUS, false);
    };
  }, [setTipEnabled]);

  return (
    <BaseLegend
      id={id}
      header={header}
      description={description}
      options={showOptions ? options : undefined}
      onChange={value => {
        // We currently only support antibiotics (Target)
        toggleGeneGroup(value as GeneGrouping as L2Target, false);
        setGrouping(value as GeneGrouping);
      }}
      defaultValue={selectedGroupingOption}
      TooltipContent={
        <UnorderedList style={{ marginBottom: '20px' }}>
          <ListItem>
            <Kbd>Click</Kbd> a label or chart bar to select a group
          </ListItem>
          {disableKeys ? undefined : <ChartHotkeyInstructions label="groups" />}
        </UnorderedList>
      }
      height={topHeight}
      onDownload={
        id
          ? () => {
              console.log('Gene legend download icon clicked, id:', id);
              downloadChartAndLegend({
                legendId: id,
                chartId: 'research-view-detected-genes-chart',
                plotType: 'bar',
                label: 'Detected_genes',
              });
              console.log('downloadChartAndLegend triggered');
            }
          : undefined
      }
    >
      <Legend
        colorScale={colorScale}
        labelFormat={labelFormat}
        getOpacity={getOpacity}
        noColor={disableKeys}
        onClick={onClick}
        // This is here to make the legend height work on the world view when it has a select element in it.
        height={legendHeight}
        updateSampleStatuses={updateSampleStatuses}
        filters={filters}
        firstLabelRef={tipAnchorRef}
      />
    </BaseLegend>
  );
}
